import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

export const useAdminQuizzes = (lessonId) => {
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (lessonId) {
      fetchQuizData();
    }
  }, [lessonId]);

  const fetchQuizData = async () => {
    try {
      // Fetch quiz
      const { data: quizData, error: quizError } = await supabase
        .from('quizzes')
        .select('*')
        .eq('lesson_id', lessonId)
        .single();

      if (quizError && quizError.code !== 'PGRST116') {
        throw quizError;
      }

      setQuiz(quizData);

      if (quizData) {
        // Fetch questions
        const { data: questionsData, error: questionsError } = await supabase
          .from('quiz_questions')
          .select('*')
          .eq('quiz_id', quizData.id)
          .order('order_index');

        if (questionsError) throw questionsError;
        setQuestions(questionsData || []);
      }
    } catch (error) {
      console.error('Error fetching quiz data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createQuiz = async (lessonId, quizData) => {
    try {
      const { data, error } = await supabase
        .from('quizzes')
        .insert([{ lesson_id: lessonId, ...quizData }])
        .select()
        .single();

      if (error) throw error;

      setQuiz(data);
      toast.success('Quiz created successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error creating quiz:', error);
      toast.error('Failed to create quiz');
      return { data: null, error };
    }
  };

  const updateQuiz = async (quizId, updates) => {
    try {
      const { data, error } = await supabase
        .from('quizzes')
        .update(updates)
        .eq('id', quizId)
        .select()
        .single();

      if (error) throw error;

      setQuiz(data);
      toast.success('Quiz updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Failed to update quiz');
      return { data: null, error };
    }
  };

  const deleteQuiz = async (quizId) => {
    try {
      const { error } = await supabase
        .from('quizzes')
        .delete()
        .eq('id', quizId);

      if (error) throw error;

      setQuiz(null);
      setQuestions([]);
      toast.success('Quiz deleted successfully');
      return { error: null };
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz');
      return { error };
    }
  };

  const createQuestion = async (quizId, questionData) => {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .insert([{ quiz_id: quizId, ...questionData }])
        .select()
        .single();

      if (error) throw error;

      setQuestions(prev => [...prev, data]);
      toast.success('Question added successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error creating question:', error);
      toast.error('Failed to create question');
      return { data: null, error };
    }
  };

  const updateQuestion = async (questionId, updates) => {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .update(updates)
        .eq('id', questionId)
        .select()
        .single();

      if (error) throw error;

      setQuestions(prev => 
        prev.map(q => q.id === questionId ? data : q)
      );
      toast.success('Question updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
      return { data: null, error };
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;

      setQuestions(prev => prev.filter(q => q.id !== questionId));
      toast.success('Question deleted successfully');
      return { error: null };
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
      return { error };
    }
  };

  return {
    quiz,
    questions,
    loading,
    createQuiz,
    updateQuiz,
    deleteQuiz,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    refetch: fetchQuizData
  };
};